import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserProfile } from '../../types';
import { Loader2, Plus, Trash2, Sun, Square, CheckSquare, CheckCircle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import Pagination from '../Pagination';

interface Horoscope {
    id: number;
    sign: string;
    vertical: string;
    period: string;
    period_start: string;
    period_end: string;
    text: string;
    published_at: string | null;
    created_at: string;
    updated_at: string;
}

interface Props {
    currentUser: UserProfile;
    horoscopeList: Horoscope[];
    onDataNeededRefresh: () => void;
    totalCount?: number;
    currentPage?: number;
    onPageChange?: (page: number) => void;
    limit: number | 'all';
    onLimitChange: (newLimit: number | 'all') => void;
}

const HoroscopeTab: React.FC<Props> = ({ currentUser, horoscopeList, onDataNeededRefresh, totalCount = 0, currentPage = 1, onPageChange, limit, onLimitChange }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    // Filters
    const [distinctSigns, setDistinctSigns] = useState<string[]>([]);
    const [distinctVerticals, setDistinctVerticals] = useState<string[]>([]);
    const [distinctPeriods, setDistinctPeriods] = useState<string[]>([]);
    
    const [selectedSigns, setSelectedSigns] = useState<Set<string>>(new Set());
    const [selectedVerticals, setSelectedVerticals] = useState<Set<string>>(new Set());
    const [selectedPeriods, setSelectedPeriods] = useState<Set<string>>(new Set());

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    const adminPath = location.pathname.split('/')[1];
    
    const totalPages = limit === 'all' ? 1 : Math.ceil(totalCount / limit);
    
    const isInitialFilterLoad = useRef(true);

    useEffect(() => {
        const fetchAndSyncFilters = async () => {
            try {
                const [signsRes, verticalsRes, periodsRes] = await Promise.all([
                    fetch('/api/cms/horoscope?distinct=sign'),
                    fetch('/api/cms/horoscope?distinct=vertical'),
                    fetch('/api/cms/horoscope?distinct=period')
                ]);
                
                if (signsRes.ok) {
                    const newDistinctSigns = await signsRes.json();
                    setDistinctSigns(newDistinctSigns);
                    if (isInitialFilterLoad.current) {
                        setSelectedSigns(new Set(newDistinctSigns));
                    } else {
                        setSelectedSigns(currentSelected => {
                            const newSelected = new Set(currentSelected);
                            let changed = false;
                            for (const sign of newSelected) {
                                if (!newDistinctSigns.includes(sign)) {
                                    newSelected.delete(sign);
                                    changed = true;
                                }
                            }
                            return changed ? newSelected : currentSelected;
                        });
                    }
                }
                
                if (verticalsRes.ok) {
                    const newDistinctVerticals = await verticalsRes.json();
                    setDistinctVerticals(newDistinctVerticals);
                     if (isInitialFilterLoad.current) {
                        setSelectedVerticals(new Set(newDistinctVerticals));
                    } else {
                        setSelectedVerticals(currentSelected => {
                            const newSelected = new Set(currentSelected);
                            for (const vertical of newSelected) {
                                if (!newDistinctVerticals.includes(vertical)) newSelected.delete(vertical);
                            }
                            return newSelected;
                        });
                    }
                }

                if (periodsRes.ok) {
                    const newDistinctPeriods = await periodsRes.json();
                    setDistinctPeriods(newDistinctPeriods);
                     if (isInitialFilterLoad.current) {
                        setSelectedPeriods(new Set(newDistinctPeriods));
                    } else {
                         setSelectedPeriods(currentSelected => {
                            const newSelected = new Set(currentSelected);
                            for (const period of newSelected) {
                                if (!newDistinctPeriods.includes(period)) newSelected.delete(period);
                            }
                            return newSelected;
                        });
                    }
                }

                if (isInitialFilterLoad.current) {
                    isInitialFilterLoad.current = false;
                }

            } catch (e) {
                console.error("Failed to fetch/sync filters", e);
            }
        };

        fetchAndSyncFilters();
    }, [horoscopeList]);


    const filteredList = useMemo(() => {
        return horoscopeList.filter(item => {
            const itemSign = (item.sign === null || item.sign === '') ? '[BLANK]' : item.sign;
            const itemVertical = (item.vertical === null || item.vertical === '') ? '[BLANK]' : item.vertical;
            const itemPeriod = (item.period === null || item.period === '') ? '[BLANK]' : item.period;

            if (selectedSigns.size > 0 && !selectedSigns.has(itemSign)) return false;
            if (selectedVerticals.size > 0 && !selectedVerticals.has(itemVertical)) return false;
            if (selectedPeriods.size > 0 && !selectedPeriods.has(itemPeriod)) return false;
    
            const itemStartDatePart = item.period_start ? item.period_start.substring(0, 10) : null;
            const itemEndDatePart = item.period_end ? item.period_end.substring(0, 10) : null;
    
            const startMatch = !startDate || !item.period_end || (itemEndDatePart && itemEndDatePart >= startDate);
            const endMatch = !endDate || !item.period_start || (itemStartDatePart && itemStartDatePart <= endDate);
    
            return startMatch && endMatch;
        });
    }, [horoscopeList, selectedSigns, selectedVerticals, selectedPeriods, startDate, endDate]);
    

    const toggleFilter = (filterSet: Set<string>, setFilter: React.Dispatch<React.SetStateAction<Set<string>>>, value: string) => {
        const newSet = new Set(filterSet);
        if (newSet.has(value)) {
            newSet.delete(value);
        } else {
            newSet.add(value);
        }
        setFilter(newSet);
    };

    const handleSelectRow = (id: number) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    };
    
    const handleSelectAllVisible = () => {
        if (selectedIds.size === filteredList.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredList.map(item => item.id)));
        }
    };
    
    const handleBulkDelete = async () => {
        if (selectedIds.size === 0 || !confirm(`Delete ${selectedIds.size} entries?`)) return;
        setLoading(true);
        try {
            const res = await fetch('/api/cms/horoscope', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: Array.from(selectedIds) })
            });
            if (res.ok) {
                setSelectedIds(new Set());
                onDataNeededRefresh();
            } else {
                alert('Delete failed.');
            }
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('pl-PL');
    }

    const columns = [
        { key: 'id', label: 'ID', width: '50px' },
        { key: 'sign', label: 'Sign' },
        { key: 'vertical', label: 'Vertical' },
        { key: 'period', label: 'Period' },
        { key: 'period_start', label: 'Start' },
        { key: 'period_end', label: 'End' },
        { key: 'text', label: 'Text', width: '200px' },
        { key: 'published_at', label: 'Published' },
        { key: 'created_at', label: 'Created' },
        { key: 'updated_at', label: 'Updated' },
    ];

    return (
        <div className="w-full relative animate-fade-in">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Sun className="w-5 h-5" /> Horoscope Manager
                    </h2>
                     <div className="text-xs text-gray-500">
                        Total: {totalCount} | Page {currentPage} of {totalPages || 1}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {selectedIds.size > 0 && (
                        <button onClick={handleBulkDelete} disabled={loading} className="flex items-center gap-1 bg-red-900/40 text-red-400 border border-red-800 hover:bg-red-900/60 px-3 py-1 rounded text-xs font-bold uppercase">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trash2 className="w-4 h-4" />} Delete ({selectedIds.size})
                        </button>
                    )}
                     <button onClick={() => navigate(`/${adminPath}/horoscope/new`)} className="flex items-center gap-1 bg-green-900/40 text-green-400 border border-green-800 hover:bg-green-900/60 px-3 py-1 rounded text-xs font-bold uppercase">
                        <Plus className="w-4 h-4" /> New Entry
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="space-y-4 bg-mystic-box p-4 rounded-lg border border-gray-800 mb-4">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-gray-400 font-bold">Sign:</span>
                        <button onClick={() => setSelectedSigns(new Set(distinctSigns))} className="text-green-400 hover:text-green-300"><CheckCircle size={16}/></button>
                        <button onClick={() => setSelectedSigns(new Set())} className="text-red-400 hover:text-red-300"><XCircle size={16}/></button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {distinctSigns.map(sign => (
                            <button key={sign} onClick={() => toggleFilter(selectedSigns, setSelectedSigns, sign)}
                                className={`text-xs px-2 py-1 rounded ${selectedSigns.has(sign) ? 'bg-mystic-accent text-black font-bold' : 'bg-gray-700 text-gray-300'}`}>
                                {sign.toLowerCase()}
                            </button>
                        ))}
                    </div>
                </div>
                 <div>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-gray-400 font-bold">Vertical:</span>
                        <button onClick={() => setSelectedVerticals(new Set(distinctVerticals))} className="text-green-400 hover:text-green-300"><CheckCircle size={16}/></button>
                        <button onClick={() => setSelectedVerticals(new Set())} className="text-red-400 hover:text-red-300"><XCircle size={16}/></button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {distinctVerticals.map(vertical => (
                            <button key={vertical} onClick={() => toggleFilter(selectedVerticals, setSelectedVerticals, vertical)}
                                className={`text-xs px-2 py-1 rounded capitalize ${selectedVerticals.has(vertical) ? 'bg-mystic-accent text-black font-bold' : 'bg-gray-700 text-gray-300'}`}>
                                {vertical.toLowerCase()}
                            </button>
                        ))}
                    </div>
                </div>
                 <div>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-gray-400 font-bold">Period:</span>
                        <button onClick={() => setSelectedPeriods(new Set(distinctPeriods))} className="text-green-400 hover:text-green-300"><CheckCircle size={16}/></button>
                        <button onClick={() => setSelectedPeriods(new Set())} className="text-red-400 hover:text-red-300"><XCircle size={16}/></button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {distinctPeriods.map(period => (
                            <button key={period} onClick={() => toggleFilter(selectedPeriods, setSelectedPeriods, period)}
                                className={`text-xs px-2 py-1 rounded capitalize ${selectedPeriods.has(period) ? 'bg-mystic-accent text-black font-bold' : 'bg-gray-700 text-gray-300'}`}>
                                {period.toLowerCase()}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="text-xs text-gray-400 font-bold">Date Range:</div>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-mystic-inset border border-gray-700 p-1 rounded text-white text-xs"/>
                        <span className="text-gray-500">-</span>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-mystic-inset border border-gray-700 p-1 rounded text-white text-xs"/>
                    </div>
                     <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-400 font-bold">Show:</label>
                        <select 
                            value={limit} 
                            onChange={e => onLimitChange(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                            className="bg-mystic-inset border border-gray-700 p-1 rounded text-white text-xs"
                        >
                            {[50, 100, 200, 500, 1000, 'all'].map(val => <option key={val} value={val}>{val}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-gray-800 rounded-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-mystic-box">
                        <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase font-bold tracking-wider">
                            <th className="p-2 border-r border-gray-800 w-10 text-center">
                                <div onClick={handleSelectAllVisible} className="cursor-pointer flex justify-center">
                                    {filteredList.length > 0 && selectedIds.size === filteredList.length ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4" />}
                                </div>
                            </th>
                            {columns.map(col => (
                                <th key={col.key} className="p-2 border-r border-gray-800 last:border-r-0" style={{ width: col.width }}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                     <tbody className="divide-y divide-gray-800 bg-mystic-bg">
                        {filteredList.map(item => (
                             <tr key={item.id} className={`group hover:bg-mystic-btn ${selectedIds.has(item.id) ? 'bg-blue-900/10' : ''}`}>
                                <td className="p-2 border-r border-gray-800 text-center">
                                    <div onClick={() => handleSelectRow(item.id)} className="cursor-pointer flex justify-center">
                                        {selectedIds.has(item.id) ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4 text-gray-700 group-hover:text-gray-500" />}
                                    </div>
                                </td>
                                {columns.map(col => {
                                    const value = item[col.key as keyof Horoscope];
                                    let displayValue: React.ReactNode = typeof value === 'string' ? value.toLowerCase() : value;
                                    
                                    if (col.key === 'id') {
                                        displayValue = (
                                            <button onClick={() => navigate(`/${adminPath}/horoscope/edit/${item.id}`)} className="font-bold text-mystic-accent hover:underline text-left">
                                                {item.id}
                                            </button>
                                        );
                                    } else if (col.key.endsWith('_at')) {
                                        displayValue = formatDate(value as string | null);
                                    } else if (col.key === 'text' && typeof value === 'string') {
                                        displayValue = <span className="text-gray-500 italic">{value.substring(0, 50).toLowerCase()}...</span>;
                                    }

                                    return (
                                        <td key={col.key} className="p-2 border-r border-gray-800 last:border-r-0 text-xs text-gray-300">
                                            {displayValue}
                                        </td>
                                    );
                                })}
                             </tr>
                        ))}
                     </tbody>
                </table>
            </div>

            {totalPages > 1 && onPageChange && (
                <div className="mt-4">
                    <Pagination 
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={onPageChange}
                    />
                </div>
            )}
        </div>
    );
};
export default HoroscopeTab;
